import { DispatcherEvent } from '@carvana/merch-native';
import { useCallback } from 'react';
import {
  CTA_GUARD_ACTION,
  CtaGuard,
  CtaGuardOutcome,
  PURCHASE_CALLER_IDENTIFIER
} from '~/constants/common/ctaGuard';
import OrderState from '~/constants/common/OrderState';
import { useIsLoggedIn } from '~/context/common/UserContext';
import {
  useClosedAccountModal,
  useOpenAccountModal
} from '~/hooks/common/accountModal/useAccountModal';
import { useGetRcbUserPurchasingGetter } from '~/hooks/vdp/services/useGetUserPurchasing';
import { type PurchaseEventPayload } from '~/native/common/contracts';
import { logInfo } from '~/utilities/loggers';

type OnContinue = () => void;

export default function useLogInAndStartPurchase(
  eventPayload: PurchaseEventPayload
) {
  const closedAccountModal = useClosedAccountModal();
  const getUserPurchasing = useGetRcbUserPurchasingGetter();
  const isLoggedIn = useIsLoggedIn();
  const openAccountModal = useOpenAccountModal();

  return useCallback(
    (confirmContinue: OnContinue, onContinue: OnContinue) => () => {
      if (isLoggedIn) {
        logInfo(CTA_GUARD_ACTION, {
          guard: CtaGuard.Login,
          isLoggedIn,
          outcome: CtaGuardOutcome.Passed
        });

        return onContinue();
      }

      logInfo(CTA_GUARD_ACTION, {
        guard: CtaGuard.Login,
        isLoggedIn,
        outcome: CtaGuardOutcome.Opened
      });

      openAccountModal({
        callerIdentifier: PURCHASE_CALLER_IDENTIFIER,
        nativeOptions: {
          eventDispatch: DispatcherEvent.StartPurchase,
          eventPayload
        },
        onSuccess: async () => {
          const purchaseResult = await getUserPurchasing();

          closedAccountModal('submit');

          const isReserved = purchaseResult?.orderState === OrderState.Reserved;

          logInfo(CTA_GUARD_ACTION, {
            guard: CtaGuard.Login,
            outcome: CtaGuardOutcome.Success
          });

          // isReserved cannot be true here: getUserPurchasing captures isLoggedIn from the click
          // render, false on this path, so it returns None without fetching. To-do: REO-9082 will
          // remove this branch.
          if (isReserved) {
            confirmContinue();
          } else {
            onContinue();
          }
        }
      });
    },
    [
      closedAccountModal,
      eventPayload,
      getUserPurchasing,
      isLoggedIn,
      openAccountModal
    ]
  );
}
